import { useMutation, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import Button from 'shopper/components/Button';
import Icon from 'shopper/components/Icon';
import Text from 'shopper/components/Text';

import useQueryClient from 'hooks/useQueryClient';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';
import { threadQueryKeys } from 'lib/queryKeys';

import { useUser } from 'providers/UserProvider';

import {
  getUserThreadSubscription,
  postSubscribeUserThread,
} from 'services/thread';

import ThreadFeedback from './Feedback';

const ThreadMainContentHeadFooter = ({
  threadComments,
  threadId,
  threadLikes,
  threadTypeName,
  threadViews,
  onCommentsButtonClick,
  onAddLike,
  gaEventCategory,
}) => {
  const { isLoggedIn, loadingLoggedInUser, runCallbackIfLoggedIn } = useUser();
  const queryClient = useQueryClient();

  const { data: isSubscribed } = useQuery({
    queryKey: threadQueryKeys.isSubscribedForNotifications({ threadId }),
    queryFn: ({ signal }) =>
      getUserThreadSubscription({ threadId }, { signal }),
    select: (data) => !!data?.isSubscribed,
    enabled: !loadingLoggedInUser && isLoggedIn,
    refetchOnMount: false,
  });

  const { mutate: mutateSubscription } = useMutation({
    mutationFn: () => postSubscribeUserThread({ threadId }),
    onMutate: () => {
      sendEvent({
        action: isSubscribed
          ? 'alert_notification_off'
          : 'alert_notification_on',
        category: gaEventCategory,
      });
      queryClient.setQueryData(
        threadQueryKeys.isSubscribedForNotifications({ threadId }),
        {
          isSubscribed: !isSubscribed,
        }
      );
    },
  });

  const onThreadSubscriptionButtonClick = () => {
    if (!isLoggedIn) {
      sendEvent({
        action: 'alert_notification_on_unlogged',
        category: gaEventCategory,
      });
    }
    runCallbackIfLoggedIn(mutateSubscription);
  };

  return (
    <>
      <div className="mt-4 flex w-full items-center border-b border-solid border-neutral-high-200 pb-6 pl-[104px] md:w-fit md:border-none md:pb-0 md:pl-24">
        <div className="mr-2 flex items-center">
          <Icon className="mr-2" name="visibility" />
          <Text size="size2">{threadViews}</Text>
        </div>
        •
        <div className="mx-2 flex items-center" size="size2">
          <Icon className="mr-2" name="like-filled" />
          <Text size="size2">{threadLikes}</Text>
        </div>
        •
        <div className="ml-2 flex items-center" size="size2">
          <Icon className="mr-2" name="chat-filled" />
          <Text size="size2">{threadComments}</Text>
        </div>
      </div>
      <div className="mt-4 flex w-full justify-end px-4 md:w-fit md:px-0">
        <ThreadFeedback
          gaEventCategory={gaEventCategory}
          threadId={threadId}
          threadTypeName={threadTypeName}
          onAddLike={onAddLike}
        />
        <Button
          className="mr-2"
          iconLeft={
            <Icon
              name={isSubscribed ? 'notification-ring' : 'notification-off'}
            />
          }
          size="size3"
          type={isSubscribed ? 'secondary' : 'secondary-stroke'}
          onClick={onThreadSubscriptionButtonClick}
        >
          {placeholder('actions.activateThreadAlert')}
        </Button>
        <Button
          className="md:hidden"
          iconLeft={<Icon name="messages" />}
          size="size3"
          type="secondary-stroke"
          onClick={onCommentsButtonClick}
        >
          {threadComments}
        </Button>
        <Button
          className="hidden md:flex"
          iconLeft={<Icon name="messages" />}
          size="size3"
          type="secondary-stroke"
          onClick={onCommentsButtonClick}
        >
          {placeholder('actions.seeThreadComments')}
        </Button>
      </div>
    </>
  );
};

ThreadMainContentHeadFooter.propTypes = {
  threadComments: PropTypes.number.isRequired,
  threadId: PropTypes.number.isRequired,
  threadLikes: PropTypes.number.isRequired,
  threadViews: PropTypes.number.isRequired,
  onCommentsButtonClick: PropTypes.func.isRequired,
  onAddLike: PropTypes.func.isRequired,
  gaEventCategory: PropTypes.string.isRequired,
};

export default ThreadMainContentHeadFooter;
